import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import DatePicker from "../../../Components/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { CancelOutlined } from "@mui/icons-material";

const callStatusOptions = [
  { value: "", label: "All Status" },
  { value: "ANSWER", label: "Answer" },
  { value: "CANCEL-AGENT", label: "Cancel-Agent" },
  { value: "CANCEL-CUSTOMER", label: "Cancel-Customer" },
  { value: "NOT CONNECTED", label: "Not Connected" },
  { value: "NON-ANSWERED", label: "Non-Answered" },
];

const filterBoxSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const filterLabelSx = {
  fontSize: "13px",
  color: "#555",
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const selectSx = {
  height: "34px",
  minWidth: "110px",
  bgcolor: "#f5f5f5",
  border: "1px solid #ddd",
  borderRadius: "6px",
  fontSize: "13px",
  color: "#000",
  "& .MuiSelect-select": {
    padding: "6px 10px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover": {
    borderColor: "#aaa",
  },
  "&.Mui-focused": {
    borderColor: "#635ee7",
  },
};

export default function CallRecordingHeader({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedDriver,
  setSelectedDriver,
  drivers,
  driversLoading,
  callStatus,
  setCallStatus,
}) {
  const [selectionDuration, setSelectionDuration] = useState("");
  const [customRangeOpen, setCustomRangeOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  const formatDateRange = (start, end) => {
    setStartDate(dayjs(start).format("YYYY-MM-DD"));
    setEndDate(dayjs(end).format("YYYY-MM-DD"));
    return `${dayjs(start).format("DD-MM-YYYY")} To ${dayjs(end).format("DD-MM-YYYY")}`;
  };

  const handleCustomRangeSubmit = () => {
    if (customStartDate && customEndDate) {
      const formattedRange = formatDateRange(customStartDate, customEndDate);
      setSelectionDuration(formattedRange);
    }
    setCustomRangeOpen(false);
  };

  const handleChangeDuration = (event) => {
    const value = event.target.value;
    const today = dayjs().format("YYYY-MM-DD");
    let formattedRange = "";

    switch (value) {
      case 0:
        formattedRange = formatDateRange(today, today);
        break;
      case 1:
        formattedRange = formatDateRange(dayjs().subtract(30, "day").format("YYYY-MM-DD"), today);
        break;
      case 2:
        formattedRange = formatDateRange(dayjs().startOf("month").format("YYYY-MM-DD"), today);
        break;
      case 3:
        formattedRange = formatDateRange(
          dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
          dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD")
        );
        break;
      case 4:
        formattedRange = formatDateRange(dayjs().subtract(90, "day").format("YYYY-MM-DD"), today);
        break;
      case 5:
        formattedRange = formatDateRange(dayjs().subtract(6, "month").format("YYYY-MM-DD"), today);
        break;
      case 6:
        formattedRange = formatDateRange(dayjs().subtract(1, "year").format("YYYY-MM-DD"), today);
        break;
      case 7:
        setCustomRangeOpen(true);
        return;
      default:
        formattedRange = "";
    }
    setSelectionDuration(formattedRange);
  };

  const handleClearBtn = () => {
    setStartDate(dayjs().startOf("month").format("YYYY-MM-DD"));
    setEndDate(dayjs().format("YYYY-MM-DD"));
    setSelectedDriver(null);
    setCallStatus("");
    setSelectionDuration("");
  };

  const hasFilters = callStatus || selectedDriver || selectionDuration;

  return (
    <Box
      sx={{
        borderBottom: "1px solid #ddd",
        px: 2,
        py: 1,
        bgcolor: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      <Box sx={{ ...filterBoxSx, flex: 1, minWidth: "200px", maxWidth: "280px" }}>
        <Autocomplete
          size="small"
          options={drivers}
          loading={driversLoading}
          getOptionLabel={(option) => option.username || ""}
          isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
          value={selectedDriver}
          onChange={(e, newValue) => setSelectedDriver(newValue)}
          sx={{ width: "100%" }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search driver..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#888", fontSize: "18px" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {driversLoading ? <CircularProgress color="inherit" size={14} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiInputBase-input": {
                  color: "#000",
                  fontSize: "13px",
                  padding: "6px 10px !important",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#999",
                  opacity: 1,
                },
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#f5f5f5",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  "& fieldset": { border: "none" },
                  "&:hover": { borderColor: "#aaa" },
                  "&.Mui-focused": { borderColor: "#635ee7" },
                },
              }}
            />
          )}
        />
      </Box>

      <Box sx={filterBoxSx}>
        <Typography sx={filterLabelSx}>Duration</Typography>
        <Select
          value={selectionDuration}
          onChange={handleChangeDuration}
          displayEmpty
          size="small"
          renderValue={(selected) => {
            if (selected === "") {
              return <span style={{ color: "#999", fontSize: "13px" }}>Select range</span>;
            }
            return <span style={{ fontSize: "13px", color: "#000" }}>{selected}</span>;
          }}
          sx={selectSx}
          IconComponent={(props) => (
            <ExpandMoreIcon {...props} sx={{ color: "#888 !important", fontSize: "18px" }} />
          )}
        >
          <MenuItem value={0}>Today</MenuItem>
          <MenuItem value={1}>Last 30 Days</MenuItem>
          <MenuItem value={2}>This Month</MenuItem>
          <MenuItem value={3}>Last Month</MenuItem>
          <MenuItem value={4}>Last 90 Days</MenuItem>
          <MenuItem value={5}>Last 6 Months</MenuItem>
          <MenuItem value={6}>Last 1 Year</MenuItem>
          <MenuItem value={7}>Custom Range</MenuItem>
        </Select>
      </Box>

      <Box sx={filterBoxSx}>
        <Typography sx={filterLabelSx}>Status</Typography>
        <Select
          value={callStatus}
          onChange={(e) => setCallStatus(e.target.value)}
          displayEmpty
          size="small"
          renderValue={(selected) => {
            if (!selected) {
              return <span style={{ color: "#999", fontSize: "13px" }}>All Status</span>;
            }
            const found = callStatusOptions.find((o) => o.value === selected);
            return <span style={{ fontSize: "13px", color: "#000" }}>{found?.label || selected}</span>;
          }}
          sx={selectSx}
          IconComponent={(props) => (
            <ExpandMoreIcon {...props} sx={{ color: "#888 !important", fontSize: "18px" }} />
          )}
        >
          {callStatusOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {hasFilters && (
        <Button
          variant="text"
          size="small"
          onClick={handleClearBtn}
          startIcon={<CancelOutlined sx={{ fontSize: "16px" }} />}
          sx={{
            textTransform: "none",
            color: "#666",
            height: "34px",
            fontSize: "13px",
            "&:hover": { bgcolor: "#f5f5f5" },
          }}
        >
          Clear
        </Button>
      )}

      <Dialog
        open={customRangeOpen}
        onClose={() => setCustomRangeOpen(false)}
      >
        <DialogContent sx={{ bgcolor: "#fff", color: "#000", minWidth: "300px" }}>
          <Stack direction="column" spacing={2}>
            <Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
              Start Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  value={customStartDate}
                  onChange={(newValue) => setCustomStartDate(newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Stack>
          <Stack direction="column" spacing={2} sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
              End Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  value={customEndDate}
                  onChange={(newValue) => setCustomEndDate(newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#fff", color: "#000" }}>
          <Button onClick={() => setCustomRangeOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleCustomRangeSubmit}
            disabled={!customStartDate || !customEndDate}
            variant="contained"
            sx={{ textTransform: "none", bgcolor: "#635ee7" }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
